import { warn } from "@swivl-mono/helpers";
import { ConversationMessage, SocketEventTicketUpdated, SocketEventType } from "@swivl-mono/types";
import { Socket, io } from "socket.io-client";

import { Member } from "../Member/Member.Current";
import { useTicketingStore } from "../Ticketing/Ticketing.Store";

/**
 * Drives this agent's presence over the **bot** socket (the single real-time
 * presence connection — see docs/snowy/plans/2026-06-18-snowy-realtime-unification.md
 * Phase 1c). Replaces the old HTTP presence-heartbeat to the server: the studio
 * authenticates to the bot with the member JWT and emits `heartbeat`/`activity`;
 * the bot runs the shared agent state machine and writes presence to Redis.
 *
 * Presence state is still *received* via the notifications `CC_PRESENCE`
 * broadcast (until Phase 4) — this manager only owns the *outbound* signals.
 *
 * Phase 2 extends this single agent socket into the unified agent transport: it
 * also subscribes to per-conversation rooms via `watch-conversation` so the
 * open conversation's messages/typing/delivery can flow live to this agent
 * (the room emits themselves land in Phase 3).
 */
/** Delivery/seen update carried by the `message-status` room event (Phase 3c). */
export interface RoomMessageStatus {
  conversationId: number;
  updates: Array<{ messageId: string; messageIndex: number; deliveryStatus?: string; seenBy?: ConversationMessage["seenBy"] }>;
}

export default class BotPresenceManager {
  private static socket: Socket | null = null;
  /** Sink for canonical conversation-room messages (realtime-unification Phase 3). */
  private static messageHandler: ((msg: ConversationMessage) => void) | null = null;
  /** Sink for delivery/seen room updates (realtime-unification Phase 3c). */
  private static statusHandler: ((payload: RoomMessageStatus) => void) | null = null;
  /**
   * Fired on every (re)connect. A SET, not a single slot (ENG-3098): both the
   * app-level presence host (re-fetching pending offers) and the Conversations
   * page (its full resync) need to recover, and with one slot whichever mounted
   * last silently won while the other's recovery never ran.
   */
  private static reconnectHandlers = new Set<() => void>();
  /**
   * Every event subscription, kept independently of the socket instance
   * (ENG-3098).
   *
   * `on()` used to attach straight to the current socket, so when `connect()`
   * replaced that socket — which it does whenever the old one is neither
   * connected nor active, e.g. after the bot restarts — every handler was
   * silently orphaned. No error, no reconnect event: realtime simply stopped
   * until the tab was reloaded. Proven by broadcasting to a live page and
   * watching nothing happen.
   *
   * That was survivable when a route component registered handlers on mount and
   * remounted often. It is not survivable now the app-level presence host
   * registers once per session.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static eventHandlers = new Map<string, Set<(...args: any[]) => void>>();

  private static get botUrl(): string {
    return import.meta.env.VITE_BOT_SERVER_URL || "http://localhost:4444";
  }

  /**
   * Open the agent presence connection to the bot. Idempotent.
   *
   * `active` matters as much as `connected` (ENG-3098): socket.io sets `active`
   * while a socket is handshaking or retrying. Bailing only on `connected` meant
   * a second caller arriving mid-handshake would close the in-flight socket and
   * start a new one — "WebSocket is closed before the connection is
   * established", plus any event delivered in that window lost.
   *
   * That never showed while ConversationsPage was the only caller. Now the
   * app-level presence host connects on mount and the CC route connects again,
   * so the two legitimately overlap.
   */
  static connect(): void {
    if (!Member.jwt || !Member.orgId) return;
    if (BotPresenceManager.socket?.connected || BotPresenceManager.socket?.active) return;
    if (BotPresenceManager.socket) BotPresenceManager.socket.close();

    BotPresenceManager.socket = io(BotPresenceManager.botUrl, {
      transports: ["websocket"],
      auth: { authType: "member", token: Member.jwt, orgId: Member.orgId },
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000
    });

    // Canonical conversation-room messages for watched conversations. The
    // listener persists across socket.io's auto-reconnects (same socket object).
    BotPresenceManager.socket.on("conversation-message", (msg: ConversationMessage) => {
      BotPresenceManager.messageHandler?.(msg);
    });
    // Ticket events (ENG-3005): live inbox/detail updates + the group toast
    // ride the bot socket's org room now — migrated off the notifications
    // server (being phased out). The cc-broadcast relay wraps the preview as
    // `{ payload }`; the store handler is unchanged from the old transport.
    BotPresenceManager.socket.on(SocketEventType.TICKET, (evt: SocketEventTicketUpdated) => {
      useTicketingStore.getState().onTicketUpdated(evt.payload);
    });
    BotPresenceManager.socket.on("message-status", (payload: RoomMessageStatus) => {
      BotPresenceManager.statusHandler?.(payload);
    });
    // Fires on initial connect + every socket.io reconnect. The agent re-joins
    // org/member rooms server-side on connect, but per-conversation watches are
    // on-demand — so the CC re-watches + catches up the open conversation here.
    // Any subscriptions registered before this socket existed (or against the
    // socket it replaces) are re-bound here.
    BotPresenceManager.attachRegisteredHandlers();

    BotPresenceManager.socket.on("connect", () => {
      // Copied before iterating: a handler may unsubscribe itself.
      for (const handler of [...BotPresenceManager.reconnectHandlers]) {
        try {
          handler();
        } catch (err) {
          warn("BotPresenceManager reconnect handler failed", err);
        }
      }
    });
  }

  /**
   * Register the sink for `conversation-message` room events. The CC store wires
   * this to its ingest so a watched conversation's messages render live off the
   * bot socket (no notify-then-fetch). Pass `null` to clear.
   */
  static onConversationMessage(handler: ((msg: ConversationMessage) => void) | null): void {
    BotPresenceManager.messageHandler = handler;
  }

  /**
   * Register the sink for `message-status` room events (delivery/seen updates).
   * Pass `null` to clear.
   */
  static onMessageStatus(handler: ((payload: RoomMessageStatus) => void) | null): void {
    BotPresenceManager.statusHandler = handler;
  }

  /**
   * Register a callback fired on every (re)connect. Returns an unsubscribe
   * function — call it on unmount rather than clearing the whole set, so one
   * component tearing down cannot cancel another's recovery.
   */
  static onReconnect(handler: () => void): () => void {
    BotPresenceManager.reconnectHandlers.add(handler);
    return () => {
      BotPresenceManager.reconnectHandlers.delete(handler);
    };
  }

  /**
   * Subscribe to a raw socket event on the agent bot socket. Used by the CC view
   * for the Snowy CC events that moved off the notifications server in Phase 4
   * (`cc:assignment`, `cc:conversation_update`, `cc:voice_event`, `cc:presence`).
   * Listeners persist across socket.io auto-reconnects (same socket object).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static on(event: string, handler: (...args: any[]) => void): void {
    // Registry first, so the handler survives a socket replacement even if it
    // is registered before the socket exists.
    const existing = BotPresenceManager.eventHandlers.get(event);
    if (existing) existing.add(handler);
    else BotPresenceManager.eventHandlers.set(event, new Set([handler]));
    BotPresenceManager.socket?.on(event, handler);
  }

  /** Remove a handler registered via {@link on}. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static off(event: string, handler: (...args: any[]) => void): void {
    BotPresenceManager.eventHandlers.get(event)?.delete(handler);
    BotPresenceManager.socket?.off(event, handler);
  }

  /**
   * Re-attach every registered handler to a freshly created socket. Without
   * this, replacing the socket silently drops all realtime.
   */
  private static attachRegisteredHandlers(): void {
    const socket = BotPresenceManager.socket;
    if (!socket) return;
    for (const [event, handlers] of BotPresenceManager.eventHandlers) {
      for (const handler of handlers) socket.on(event, handler);
    }
  }

  /** Liveness ping — keeps the agent present; the bot computes online/busy/away. */
  static heartbeat(): void {
    if (BotPresenceManager.socket?.connected) BotPresenceManager.socket.emit("heartbeat");
  }

  /** Agent did something interactive (e.g. sent a message) — resets the idle timer. */
  static recordActivity(): void {
    if (BotPresenceManager.socket?.connected) BotPresenceManager.socket.emit("activity");
  }

  /**
   * Start watching a conversation's room so its live traffic reaches this agent.
   * No-op if the socket isn't connected yet (re-issued when a conversation is
   * opened; the bot authorizes org ownership before joining). Idempotent.
   */
  static watchConversation(conversationId: number): void {
    if (BotPresenceManager.socket?.connected) BotPresenceManager.socket.emit("watch-conversation", { conversationId });
  }

  /** Stop watching a conversation's room (agent switched away / closed it). */
  static unwatchConversation(conversationId: number): void {
    if (BotPresenceManager.socket?.connected) BotPresenceManager.socket.emit("unwatch-conversation", { conversationId });
  }

  /**
   * Tell the bot the agent is viewing this conversation, so the user's
   * messages are marked seen by this member and the user's widget advances
   * its ticks to "seen". No-op if the socket isn't connected.
   */
  static markRead(conversationId: number): void {
    if (BotPresenceManager.socket?.connected) BotPresenceManager.socket.emit("mark-read", { conversationId });
  }

  /** Tear down the connection — presence expires via the bot's TTL sweep. */
  static disconnect(): void {
    if (BotPresenceManager.socket) {
      BotPresenceManager.socket.close();
      BotPresenceManager.socket = null;
    }
  }
}
